import ISSUE_VIEWER_VIEW_QUERY from '@github-ui/issue-viewer/IssueViewerViewQuery.graphql'
import type {NavigatorRouteRegistration} from '@github-ui/react-core/app-routing-types'
import {registerNavigatorApp} from '@github-ui/react-core/register-app'
import {relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {relayRoute} from '@github-ui/relay-route'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {useEffect} from 'react'

import {App} from './App'
import {clientSideRelayDataGenerator, setRecordMap} from './pages/ClientSideRelayDataGenerator'
import {IssueShowPage} from './pages/IssueShowPage'
import {VARIABLE_TRANSFORMERS} from './utils/variable-transformers'

const isDevelopment = () => process.env.NODE_ENV === 'development'

// Prevent `/:owner/:repo/issues/:number` from treating "new" as an issue number when navigating
// from the Relay app. A full page load lets the server hand the route to the Data Router app.
function HardNavigate() {
  useEffect(() => {
    ssrSafeWindow?.location.assign(ssrSafeWindow.location.href)
  }, [])
  return null
}

function withSharedComponent(route: NavigatorRouteRegistration, alias: NavigatorRouteRegistration) {
  return [route, {...alias, Component: route.Component}]
}

registerNavigatorApp('issues-react', () => {
  const relayEnvironment = relayEnvironmentWithMissingFieldHandlerForNode()
  setRecordMap(relayEnvironment)
  clientSideRelayDataGenerator({environment: relayEnvironment})

  const relayRouteConfig = {
    fallback: isDevelopment() ? 'Loading...' : '',
    relayEnvironment,
  }
  const issueShowRouteConfig = {
    ...relayRouteConfig,
    resourceName: 'IssueShow',
    // The issue viewer manages its own document title (from the issue's title), so this route
    // intentionally provides an empty title to opt out of the generic title manager.
    title: '',
    Component: IssueShowPage,
    transformVariables: VARIABLE_TRANSFORMERS['/:owner/:repo/issues/:number'],
    maxAge: 10,
    cacheTTL: DEFAULT_RELAY_CACHE_TTL,
    exclude_from_cache_key: ['allowedOwner'],
  }
  const issueViewerViewQueryVariables = (routeParams: {pathParams: Record<string, string | undefined>}) => ({
    owner: routeParams.pathParams['owner'],
    repo: routeParams.pathParams['repo'],
    number: routeParams.pathParams['number'] ? parseInt(routeParams.pathParams['number']) : undefined,
  })
  return {
    App,
    routes: [
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:name/issues/new',
        resourceName: 'IssueRepoNew',
        title: 'New Issue',
        Component: HardNavigate,
        queryConfigs: {},
      }),
      relayRoute({
        ...relayRouteConfig,
        path: '/:owner/:name/issues/new/choose',
        resourceName: 'IssueRepoNewChoose',
        title: 'Choose an Issue Template',
        Component: HardNavigate,
        queryConfigs: {},
      }),
      // Both paths stay inline `relayRoute` calls so the route-to-query manifest preloads them on the server.
      // The artifact path reuses the issue route's Component so opening or closing an artifact does not
      // remount the issue. The server validates the artifact number and handles transfers.
      ...withSharedComponent(
        relayRoute({
          ...issueShowRouteConfig,
          path: '/:owner/:repo/issues/:number',
          queryConfigs: {
            issueViewerViewQuery: {
              concreteRequest: ISSUE_VIEWER_VIEW_QUERY,
              variableMappers: issueViewerViewQueryVariables,
            },
          },
        }),
        relayRoute({
          ...issueShowRouteConfig,
          path: '/:owner/:repo/issues/:number/artifacts/:artifactNumber',
          queryConfigs: {
            issueViewerViewQuery: {
              concreteRequest: ISSUE_VIEWER_VIEW_QUERY,
              variableMappers: issueViewerViewQueryVariables,
            },
          },
        }),
      ),
    ],
  }
})
